import { licenseCheckLatestStatusEntityName, licenseCheckLatestStatusListViewName, verifyCredentialsBulkActionType, } from '../../../../../backend/features/credentialing-verification/shared';
import { useLicenseCheckLatestStatusListViewExport } from '../../../../../ui/features/credentialing-verification/hooks/useLicenseCheckLatestStatusListViewExport';
import { useManualCredentialVerificationExpiringOnlyFilter } from '../../../../../ui/features/credentialing-verification/hooks/useManualCredentialVerificationExpiringOnlyFilter';
import { useCredentialingTranslation } from '../../../../../ui/hooks';
import { CredentialingContext } from '../../../../../ui/providers/credentialing-context';
import { userCanMessageNonMembers } from '@juvare/alert-ui/backend/features/permissions/shared';
import { DataTable, useDataTableUsingUrl } from '@juvare/common-data-table-ui/DataTable';
import { FiltersBar } from '@juvare/common-data-table-ui/FiltersBar';
import { showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { ModalSlideout } from '@juvare/common-web-ui/ModalSlideout';
import { maxUrlSearchLength } from '@juvare/contact-management/utils';
import { launchBulkAction } from '@juvare/platform-sdk-react/ui/features/bulk-actions';
import { useStorageClient, useViewDataTable } from '@juvare/platform-sdk-react/ui/hooks';
import { getUserState } from '@juvare/platform-sdk-react/ui/stores';
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { Box, FormControlLabel, Switch } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useLicenseCheckLatestStatusListViewColumns } from '../../hooks/useLicenseCheckLatestStatusColumns';
import { useLicenseCheckLatestStatusFilterConfig } from '../../hooks/useLicenseCheckLatestStatusFilterConfig';
import { filterDataToAccountFilter } from '../../utils/filterDataToAccountFilter';
import { mapLicenseCheckLatestStatusFilter } from '../../utils/mapLicenseCheckLatestStatusFilter';
import { showNotificationDialogWithRecipients } from '../../utils/showNotificationDialogWithRecipients';
export const CredentialVerificationDetails = ({ userDisplayName, reviewProfileTabName }) => {
    const { t } = useCredentialingTranslation();
    const { personId, onPersonSelect, onPersonReset, personDetailsComponent: PersonDetailsComponent, } = React.useContext(CredentialingContext);
    const [searchParams] = useSearchParams();
    const userState = useSelector(getUserState);
    const columns = useLicenseCheckLatestStatusListViewColumns(reviewProfileTabName);
    const filterConfig = useLicenseCheckLatestStatusFilterConfig(userDisplayName);
    const { canEnableExpiringOnlyFilter, expiringOnlyFilter, isExpiringOnlyFilterChecked, handleShowExpiringOnlyFilterChange } = useManualCredentialVerificationExpiringOnlyFilter();
    const { dataTableProps, filtersBarProps, clearActiveRow, appliedFilterData, reload } = useViewDataTable({
        title: t('credentialVerificationsPage.title'),
        viewName: licenseCheckLatestStatusListViewName,
        mapViewFilterData: (filterData) => {
            const filter = mapLicenseCheckLatestStatusFilter(filterData);
            if (expiringOnlyFilter) {
                filter.push(...expiringOnlyFilter);
            }
            return filter;
        },
        filterConfig,
        columns,
        defaultSortColumnKey: 'requestDate',
    }, {
        useDataTableFunction: useDataTableUsingUrl,
        maxUrlSearchLength,
        rowSelectionEnabled: true,
        onRowClick: ({ personId }) => onPersonSelect(personId),
        defaultSortColumnDesc: true,
    });
    const licenseCheckLatestStatusOrderBy = dataTableProps.sortColumnKey
        ? [
            {
                column: dataTableProps.sortColumnKey,
                desc: dataTableProps.sortColumnDesc,
            },
        ]
        : undefined;
    const { handleExportLicenseCheckLatestStatuses: handleExportAll } = useLicenseCheckLatestStatusListViewExport({
        orderBy: licenseCheckLatestStatusOrderBy,
        filter: [
            {
                column: 'personSystemStatus',
                op: '=',
                value: 'active',
            },
        ],
    });
    const { handleExportLicenseCheckLatestStatuses: handleExport } = useLicenseCheckLatestStatusListViewExport({
        filter: dataTableProps.selectedRowIds && dataTableProps.selectedRowIds.length > 0
            ? [
                {
                    column: 'id',
                    op: 'in',
                    value: dataTableProps.selectedRowIds,
                },
            ]
            : [...mapLicenseCheckLatestStatusFilter(appliedFilterData), ...(expiringOnlyFilter ? expiringOnlyFilter : [])],
        orderBy: licenseCheckLatestStatusOrderBy,
    });
    const licenseCheckLatestStatusStorage = useStorageClient(licenseCheckLatestStatusEntityName);
    const id = searchParams.get('id');
    React.useEffect(() => {
        if (!personId && id) {
            licenseCheckLatestStatusStorage
                .get(id)
                .then((entity) => onPersonSelect(entity.data.personId))
                .catch(() => {
                // Ignore
            });
        }
    }, [id, licenseCheckLatestStatusStorage, onPersonSelect, personId]);
    const handlePersonDetailsSlideoutClose = () => {
        clearActiveRow();
        onPersonReset();
    };
    const handleBulkNotify = () => {
        var _a;
        const accountsFilter = ((_a = dataTableProps.selectedRowIds) === null || _a === void 0 ? void 0 : _a.length)
            ? [
                {
                    op: 'in',
                    idSubFilter: {
                        entity: 'licenseCheckLatestStatus',
                        column: 'personId',
                        filter: [
                            {
                                op: 'in',
                                id: dataTableProps.selectedRowIds,
                            },
                        ],
                    },
                },
            ]
            : filterDataToAccountFilter(appliedFilterData);
        showNotificationDialogWithRecipients(t, {
            includeNonMembers: userCanMessageNonMembers(userState),
            accounts: [],
            accountsFilter: accountsFilter,
            organizations: [],
            groups: [],
        });
    };
    const handleBulkVerifyCredentials = () => {
        var _a;
        const filter = ((_a = dataTableProps.selectedRowIds) === null || _a === void 0 ? void 0 : _a.length)
            ? [
                {
                    op: 'in',
                    idSubFilter: {
                        entity: 'licenseCheckLatestStatus',
                        column: 'personId',
                        filter: [
                            {
                                op: 'in',
                                id: dataTableProps.selectedRowIds,
                            },
                        ],
                    },
                },
            ]
            : filterDataToAccountFilter(appliedFilterData);
        launchBulkAction({
            type: verifyCredentialsBulkActionType,
            title: t('table.credentialingVerification.actions.verifyCredentialsDialog.bulkActionTitle'),
            params: {
                requestedBy: userDisplayName,
                filter: filter,
            },
            onClose: () => {
                var _a;
                (_a = dataTableProps.onDeselectAllRows) === null || _a === void 0 ? void 0 : _a.call(dataTableProps);
                reload();
            },
        });
    };
    return (React.createElement(Box, { p: 4 },
        React.createElement(DataTable, Object.assign({}, dataTableProps, { filtersBar: React.createElement(FiltersBar, Object.assign({}, filtersBarProps, { config: filterConfig })), tableSx: { tableLayout: 'fixed' }, globalActions: [
                {
                    icon: React.createElement(FileUploadOutlinedIcon, null),
                    label: t('table.credentialingVerification.actions.exportAll'),
                    onClick: handleExportAll,
                    'data-test': 'export-global-action-button',
                },
            ], localActions: [
                {
                    icon: React.createElement(FileUploadOutlinedIcon, null),
                    label: t('table.credentialingVerification.actions.export'),
                    onClick: handleExport,
                    'data-test': 'export-local-action-button',
                },
                {
                    icon: React.createElement(SendOutlinedIcon, null),
                    label: t('table.credentialingVerification.actions.notify'),
                    onClick: handleBulkNotify,
                    'data-test': 'notify-local-action-button',
                },
                {
                    icon: React.createElement(ContentPasteSearchIcon, null),
                    label: t('table.credentialingVerification.actions.verifyCredentials'),
                    onClick: () => {
                        showConfirmDialog({
                            title: t('table.credentialingVerification.actions.verifyCredentialsDialog.title'),
                            content: t('table.credentialingVerification.actions.verifyCredentialsDialog.content'),
                            onConfirm: handleBulkVerifyCredentials,
                            confirmLabel: t('table.credentialingVerification.actions.verifyCredentialsDialog.confirmLabel'),
                        });
                    },
                    'data-test': 'verify-credentials-local-action-button',
                },
            ], customHeaderElement: canEnableExpiringOnlyFilter && (React.createElement(FormControlLabel, { sx: { mt: -2, mb: -2 }, label: t('credentialVerificationsPage.showExpiringOnly'), control: React.createElement(Switch, { checked: isExpiringOnlyFilterChecked, onChange: handleShowExpiringOnlyFilterChange }) })) })),
        personId && (React.createElement(ModalSlideout, { open: !!personId, onClose: handlePersonDetailsSlideoutClose, "data-test": 'person-details-slideout' }, personId && PersonDetailsComponent && React.createElement(PersonDetailsComponent, { accountId: personId })))));
};
